import { toast } from "@nexxt/ui";
import { m } from "../paraglide/messages.js";

// Undo (ADR-282). For a few seconds after the save of a grid its toast offers "Undo", and ⌘Z / Ctrl Z outside a field
// does the same. The undo is the same operation again, with the values before and the version the save answered: a
// row someone changed since is a 409, which says who did, and nothing is undone. One undo at a time: the last offered.

/** How long the toast of a save offers its undo; sonner stops the time while the pointer is on it. */
export const UNDO_MS = 10_000;

/**
 * One toast at a time of a kind: while it is on screen a new one takes its id, and sonner updates it in place; once it
 * has started to leave (closed, gone by itself, or replaced by an undo) the next one has a new id. With one id for
 * all, a toast shown during the exit of the one before was merged into it by sonner and closed with it.
 */
function toastSlot(kind: string) {
  let seq = 0;
  let live: string | null = null;
  return {
    id: (): string => (live ??= `nexxt-${kind}-${++seq}`),
    /** The toast `id` has started to leave: its id is not reused. */
    gone: (id: string): void => {
      if (live === id) live = null;
    },
    dismiss: (): void => {
      if (live !== null) toast.dismiss(live);
      live = null;
    },
  };
}

const offerToast = toastSlot("undo");
const doneToast = toastSlot("undone");

/** The inverse of a write: the same operation again; it rejects when it is refused. */
type Undo = () => Promise<unknown>;

/** What a write that can be undone carries besides its input: the hooks read it, the call never sends it. */
export interface UndoVars<R> {
  /** The inverse of this write, from what it answered; null when it has none. */
  undo?: ((result: R) => Undo | null) | undefined;
  /**
   * This write is (a step of) the inverse of another: its success says nothing, the undo says "Undone" once all its
   * steps are done; its refusal "Not undone" (`reportError`).
   */
  undoing?: boolean | undefined;
}

/** The variables of a mutation whose writes can be undone. */
export type Undoable<V, R> = V & UndoVars<R>;

/** The `mutationFn` of a mutation whose writes can be undone: the call gets its input, without what the hooks read. */
export const undoableCall =
  <V, R>(call: (input: V) => Promise<R>) =>
  ({ undo: _undo, undoing: _undoing, ...input }: Undoable<V, R>): Promise<R> =>
    call(input as V);

/** Whether the variables of a write, any write, are those of an undo. */
export const isUndoing = (vars: unknown): boolean =>
  typeof vars === "object" && vars !== null && (vars as UndoVars<never>).undoing === true;

let offered: Undo | null = null;

/**
 * The toast of a write that succeeded: `text`, with "Undo" when the write has an inverse (`show` is the toast of the
 * outcome, a warning when the user must look at it); nothing for a step of an undo. A write without an inverse takes
 * away the undo offered before: ⌘Z would undo a save that is no longer the last.
 */
export function toastSaved<R>(
  text: string,
  result: R,
  vars: UndoVars<R> | undefined,
  show: typeof toast.success = toast.success,
): void {
  if (vars?.undoing) return;
  const undo = vars?.undo?.(result) ?? null;
  if (!undo) {
    offered = null;
    offerToast.dismiss();
    return void show(text);
  }
  offered = undo;
  const id = offerToast.id();
  const forget = () => {
    if (offered === undo) offered = null;
    offerToast.gone(id);
  };
  show(text, {
    id,
    duration: UNDO_MS,
    action: { label: m.undo(), onClick: () => runUndo() },
    onDismiss: forget,
    onAutoClose: forget,
  });
}

/**
 * Runs the undo offered, from its toast or from ⌘Z; false when there is none. "Undone" once every step of it is
 * done; a refusal is the hook's to say (`reportError`), with who changed the row.
 */
export function runUndo(): boolean {
  const undo = offered;
  if (!undo) return false;
  offered = null;
  offerToast.dismiss();
  undo().then(
    () => {
      const id = doneToast.id();
      const gone = () => doneToast.gone(id);
      toast.success(m.undo_done(), { id, onDismiss: gone, onAutoClose: gone });
    },
    () => {},
  );
  return true;
}
